import I18n from '@/i18n';
import type { AiPhotoUploadResponse, UploadFailure } from '@/types/Upload';
import { getCsrfToken } from '@/utils/csrf';

/** Checked before the transfer so an unacceptable photo never leaves the browser. */
const allowedPhotoExtensions = ['jpg', 'jpeg', 'png', 'webp'];
const maxPhotoSizeBytes = 100 * 1024 * 1024;
const minPhotoSizeBytes = 1;

const translationPrefix = 'pages.product_detail_page.ai_upload.button.errors';

function genericErrorMessage(): string {
    return I18n.global.t('pages.product_detail_page.product_info.product_button.upload.errors.generic');
}

// Mirrors AiPhotoUploadRequest's rules so the customer is told before paying the bandwidth.
export function photoRejectionKey(file: File): string | undefined {
    const extension = file.name.split('.').pop()?.toLowerCase();

    if (!extension || !allowedPhotoExtensions.includes(extension)) {
        return `${translationPrefix}.invalid_file_type`;
    }

    if (file.size < minPhotoSizeBytes) {
        return `${translationPrefix}.file_too_small`;
    }

    if (file.size > maxPhotoSizeBytes) {
        return `${translationPrefix}.file_too_large`;
    }

    return undefined;
}

/** Raised for anything the caller should tell the customer about; aborts are signalled separately. */
export class AiPhotoUploadError extends Error {
    public constructor(public readonly failure: UploadFailure) {
        super(failure.message);
        this.name = 'AiPhotoUploadError';
    }
}

export class AiPhotoUploadAbortedError extends Error {
    public constructor() {
        super('AI photo upload aborted');
        this.name = 'AiPhotoUploadAbortedError';
    }
}

type UploadErrorPayload = {
    message?: string;
    errors?: { file?: string[] };
};

function failureFromResponse(responseText: string, status: number): UploadFailure {
    try {
        const parsed = JSON.parse(responseText) as UploadErrorPayload;

        if (parsed.errors?.file?.[0]) {
            return { message: parsed.errors.file[0], status };
        }

        if (parsed.message) {
            return { message: parsed.message, status };
        }
    } catch (_error: unknown) {
        // A non-JSON body (a proxy error page, a 413 from the web server) falls through.
    }

    return { message: genericErrorMessage(), status };
}

function bodyFor(file: File, groupIdentifier?: string): FormData {
    const body = new FormData();
    body.append('file', file, file.name);

    if (groupIdentifier !== undefined) {
        body.append('group_identifier', groupIdentifier);
    }

    return body;
}

function openRequest(endpoint: string): XMLHttpRequest {
    const request = new XMLHttpRequest();

    request.open('POST', endpoint, true);
    request.withCredentials = true;
    request.setRequestHeader('Accept', 'application/json');
    request.setRequestHeader('X-XSRF-TOKEN', getCsrfToken());

    return request;
}

function trackTransferProgress(request: XMLHttpRequest, onTransferProgress: (percentage: number) => void, onTransferComplete: () => void): void {
    request.upload.onprogress = (event: ProgressEvent): void => {
        if (!event.lengthComputable) {
            return;
        }

        onTransferProgress(Math.floor((event.loaded / event.total) * 100));
    };

    // The bytes are gone; storing, sniffing and queueing the generation report nothing.
    request.upload.onload = (): void => onTransferComplete();
}

function abortRequestWith(request: XMLHttpRequest, signal: AbortSignal | undefined): void {
    signal?.addEventListener('abort', () => request.abort(), { once: true });
}

function parseSuccess(responseText: string): AiPhotoUploadResponse {
    try {
        return JSON.parse(responseText) as AiPhotoUploadResponse;
    } catch (_error: unknown) {
        throw new AiPhotoUploadError({ message: genericErrorMessage() });
    }
}

function settleOnCompletion(request: XMLHttpRequest, resolve: (value: AiPhotoUploadResponse) => void, reject: (error: unknown) => void): void {
    request.onload = (): void => {
        if (request.status < 200 || request.status >= 300) {
            reject(new AiPhotoUploadError(failureFromResponse(request.responseText, request.status)));

            return;
        }

        resolve(parseSuccess(request.responseText));
    };

    request.onerror = (): void => reject(new AiPhotoUploadError({ message: genericErrorMessage() }));
    request.onabort = (): void => reject(new AiPhotoUploadAbortedError());
}

/** XHR rather than fetch: only XHR reports bytes sent, which is what drives the bar. */
export function uploadAiPhoto(
    file: File,
    endpoint: string,
    groupIdentifier: string | undefined,
    onTransferProgress: (percentage: number) => void,
    onTransferComplete: () => void,
    signal?: AbortSignal,
): Promise<AiPhotoUploadResponse> {
    return new Promise((resolve, reject) => {
        const request = openRequest(endpoint);
        trackTransferProgress(request, onTransferProgress, onTransferComplete);
        abortRequestWith(request, signal);
        settleOnCompletion(request, resolve, reject);
        request.send(bodyFor(file, groupIdentifier));
    });
}
